import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import { types, Area, UpdateAreaRequest, CreateAreaRequest, DeleteAreaRequest, GetAreasRequest } from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* getAreasAsync({ payload }: GetAreasRequest) {
  yield put({ type: types.GET_AREAS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/areas`);
    const response: Area[] = yield call(api.getAll, payload);

    yield put({ type: types.GET_AREAS.SUCCESS, payload: response });
  } catch (err) {
    put({ type: types.GET_AREAS.ERROR, payload: 'Failed to get areas' });
  } finally {
    yield put({ type: types.GET_AREAS.PENDING, payload: false });
  }
}

export function* createAreaAsync({ payload }: CreateAreaRequest) {
  yield put({ type: types.CREATE_AREA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const areaApi = new API(`clients/${clientId}/areas`);
    yield call(areaApi.create, payload);

    yield put({ type: types.CREATE_AREA.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_AREA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_AREA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_AREA.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_AREA.PENDING, payload: false });
  }
}

export function* updateAreaAsync({ payload }: UpdateAreaRequest) {
  yield put({ type: types.UPDATE_AREA.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const areaApi = new API(`clients/${clientId}/areas`);
    const response: Area = yield call(areaApi.update, payload);

    yield put({ type: types.UPDATE_AREA.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_AREA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_AREA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_AREA.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_AREA.PENDING, payload: false });
  }
}

export function* deleteAreaAsync({ payload }: DeleteAreaRequest) {
  yield put({ type: types.DELETE_AREA.PENDING, payload: true });

  try {
    const { id } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const areaApi = new API(`clients/${clientId}/areas`);
    yield call(areaApi.destroy, id);

    yield put({ type: types.DELETE_AREA.SUCCESS, payload: { id } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_AREA.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_AREA.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_AREA.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_AREA.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_AREAS.REQUEST, getAreasAsync),
  takeLatest(types.UPDATE_AREA.REQUEST, updateAreaAsync),
  takeLatest(types.CREATE_AREA.REQUEST, createAreaAsync),
  takeLatest(types.DELETE_AREA.REQUEST, deleteAreaAsync)
];
